// !NOTE: Merge with RevstackCategoryPerformanceWidget.tsx
import React, { useEffect, useRef, useState, useMemo, ReactElement } from "react";
import WidgetCard from "./WidgetCard";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { GraphTypes, RevstackFilterTypes, WidgetType } from "@ts/common-types";
import { w3WidgetFormatter, w5WidgetFormatter } from "@/ts/revstack/formatters";

interface RevstackBrandwiseDistributionWidgetProps {
    title: string;
    widgetData: any;
    widgetConfig: any;
    widgets: any;
    isTitleRequired?: boolean;
    isCardRequired?: boolean;
    icon?:ReactElement;
}

const RevstackBrandwiseDistributionWidget: React.FC<
    RevstackBrandwiseDistributionWidgetProps
> = ({
    title,
    widgetData,
    widgetConfig,
    widgets,
    isTitleRequired = true,
    isCardRequired = true,
    icon,
}) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartComponentRef = useRef<any>(null);
    const chartTypeFilterOptions = useMemo(() => {
        return widgetConfig?.config?.filters?.[0]?.filterOptions;
    }, [widgetConfig]);
    //!Note: @sarvesh refactor...
    const [chartType, setChartType] = useState(widgetConfig?.config?.filters?.[0]?.defaultFilter || GraphTypes.TREND);

    // Memoized computation of active widget and chart data
    const { activeWidget, chartData } = useMemo(() => {
        if (!widgets || !widgetData) {
            return { activeWidget: null, chartData: null };
        }

        // Flatten widgets array and find the active widget
        const flattenedWidgets = widgets.flat();
        const activeWidget = flattenedWidgets.find((widgetList: any) => {
            const shouldShowWidget =
                (chartType === GraphTypes.TREND &&
                    widgetList.type === WidgetType.W3) ||
                (chartType === GraphTypes.PIE &&
                    widgetList.type === WidgetType.W5);
            return shouldShowWidget;
        });

        if (!activeWidget) {
            return { activeWidget: null, chartData: null };
        }

        // Compute chart data based on chart type
        //!Note[DND]: this should be dynamic this case have to be handled in config
        const chartData =
            chartType === GraphTypes.TREND
                ? w3WidgetFormatter(widgetConfig, activeWidget, widgetData)?.componentData
                : w5WidgetFormatter(activeWidget, widgetData);

        return { activeWidget, chartData };
    }, [widgets, widgetData, chartType]);

    // Memoized subtitle computation
    const subTitle = useMemo(() => {
        if (!chartData?.selectedBrandsRank) return " ";

        if (chartType === GraphTypes.PIE) {
            return `My Brand Ranked ${chartData.selectedBrandsRank}th`;
        }
        if (chartType === GraphTypes.TREND) {
            return `My Brand Ranked ${chartData.selectedBrandsRank}th in respect of MS`;
        }
        return "";
    }, [chartData?.selectedBrandsRank, chartType]);

    useEffect(() => {
        if (!chartContainerRef.current || !chartComponentRef.current) return;
        const observer = new window.ResizeObserver(() => {
            if (chartComponentRef.current && chartComponentRef.current.chart) {
                chartComponentRef.current.chart.reflow();
            }
        });
        observer.observe(chartContainerRef.current);
        return () => observer.disconnect();
    }, []);

    // Render the widget component
    const renderWidgetComponent = () => {
        if (!activeWidget || !chartData) return null;

        if (!activeWidget.widgetComponent) {
            console.warn(
                `Widget component not found for type: ${activeWidget.type}`
            );
            return null;
        }

        const WidgetComponent = activeWidget.widgetComponent;

        return (
            <WidgetComponent
                key={`${activeWidget.type}-${
                    activeWidget.identifier || "widget"
                }`}
                widget={activeWidget}
                chartComponentRef={chartComponentRef}
                widgetData={
                    chartType === GraphTypes.PIE
                        ? chartData?.chartDataSeries
                        : chartData
                }
                graphColors={chartData?.graphColors}
                isLoading={chartData?.isLoading}
                selectedBrands={
                    chartType === GraphTypes.PIE
                        ? chartData?.selectedBrands
                        : undefined
                }
                config={activeWidget.config}
            />
        );
    };

    return (
        <WidgetCard
            overwriteDefaultClass={!isCardRequired}
            className={
                !isCardRequired
                    ? "bg-transparent border-0 rounded-0 shadow-none"
                    : ""
            }
            style={{ height: "350px" }}
        >
            {isTitleRequired && (
                <div
                    className={`d-flex align-items-start justify-content-between ${
                        chartType === GraphTypes.PIE ? "mb-0" : "mb-3"
                    }`}
                >
                    <div className="d-flex align-items-start gap-1">
                        <div
                            className="icon-btn"
                            style={{
                                marginRight: 10,
                            }}
                        >
                            {icon}
                        </div>
                        <div className="d-flex flex-column justify-content-start">
                            <h4 className="mb-0">
                                {title || "Brandwise Distribution"}
                            </h4>
                            <span
                                style={{
                                    fontStyle: "italic",
                                    fontWeight: 400,
                                    fontSize: 14,
                                }}
                            >
                                {subTitle || ""}
                            </span>
                        </div>
                    </div>
                    {chartTypeFilterOptions && 
                        buildWidgetCardFilters({
                            filters: [
                                {
                                    section: "" as const,
                                    type: RevstackFilterTypes.CHART_TYPE,
                                    props: {
                                        options: chartTypeFilterOptions,
                                        value: chartType,
                                        onChange: setChartType,
                                    },
                                },
                            ]
                        })
                    }
                </div>
            )}
            <div ref={chartContainerRef} className="w-100 h-100">
                {renderWidgetComponent()}
            </div>
        </WidgetCard>
    );
};

export default RevstackBrandwiseDistributionWidget;
